import { WprBaseObject } from '../../../WprBaseObject';
import { WprBaseViewCore } from '../../WprBaseViewCore';
import { IWprConfigOption } from '../IWprConfigOption';
import { WprReflectUtil } from '../../../util/WprReflectUtil';
import { WprControlInfo } from '../WprControlInfo';
import { WprValidError } from '../../validation/WprValidError';
import { WprListControlInfo } from './WprListControlInfo';
import { WprRowInfo } from '../../../component/props/WprRowInfo';

/**
 * リスト情報
 */
export class WprListInfo extends WprBaseObject {
	// private 変数  ------------------------------------------------------------
	private m_Name: string									= null;										// リスト名
	private m_ListData: Array<any>							= null;										// リストデータ
	private m_ViewCore: WprBaseViewCore						= null;										// View情報
	private m_ControlMap: Map<string, WprListControlInfo>	= new Map<string, WprListControlInfo>();	// コントロール情報マップ
	// --------------------------------------------------------------------------

	// コンストラクタ  -----------------------------------------------------------
	public constructor(viewCore: WprBaseViewCore, name: string) {
		super();
		this.m_Name = name;
		this.m_ViewCore = viewCore;
	}
	// --------------------------------------------------------------------------

	// プロパティ  ---------------------------------------------------------------
	/** リスト名 */
	public get name(): string 									{ return this.m_Name; 		}
	/** リストデータ */
	public get listData(): Array<any> 							{ return this.m_ListData; 	}
	/** ビュー情報 */
	public get viewCore(): WprBaseViewCore 						{ return this.m_ViewCore; 	}
	/** コントロール情報マップ */
	public get controlMap(): Map<string, WprListControlInfo> 	{ return this.m_ControlMap; }
	/** データタイプ */
	public get dataType(): string 								{ return 'リスト'; 			}
	// --------------------------------------------------------------------------

	// public メソッド  ---------------------------------------------------------
	/**
	 * コントロール定義追加処理
	 * @param name コントロール名
	 * @param description 説明
	 * @param option オプション
	 */
	public addConfig(name: string, description: string, option?: IWprConfigOption): WprListControlInfo {
		if (this.m_ControlMap.has(name) === false) {
			const cinfo = new WprListControlInfo(this, this.m_ViewCore, name, description, option);
			this.m_ControlMap.set(name, cinfo);
			return cinfo;
		}
		else {
			if (this.m_ControlMap.get(name).useFormControl === true)
				this.m_ViewCore.addErrorLog(`同一のコントロール定義が既に登録されています。[${name}]`);
		}
		return null;
	}

	/**
	 * リストデータ設定
	 * @param ldata リストデータ
	 */
	public setListData(ldata: Array<any>) {
		// ATODE
		this.m_ListData = ldata;
		this.clear();
		for(const info of ldata) {
			this.m_ControlMap.forEach((cinfo, key, map) => {
				cinfo.addControl();
			});
		}
	}

	/**
	 * リストデータをビューに反映する
	 */
	public refreshView() {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const row of this.m_ListData) {
				for(const cinfo of this.m_ControlMap.values()) {
					cinfo.rowIndex = idx;
					if (cinfo.useFormControl === true) {
						if (WprReflectUtil.checkProperty(row, cinfo.bindName) === true) {
							const val = WprReflectUtil.getProperty(row, cinfo.bindName);
							cinfo.setControlValue(idx, val);
						}
						else {
							this.m_ViewCore.addErrorLog(`${this.dataType}データのプロパティが見つかりませんでした。[${this.m_Name}}][${cinfo.bindName}}]`);
						}
					}
				}
				idx++;
			}
		}
	}

	/**
	 * ビューをリストデータに反映する
	 */
	public refreshModel() {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const row of this.m_ListData) {
				for(const cinfo of this.m_ControlMap.values()) {
					cinfo.rowIndex = idx;
					if (cinfo.useFormControl === true) {
						if (WprReflectUtil.checkProperty(row, cinfo.bindName) === true) {
							const val = cinfo.getControlValue(idx);
							WprReflectUtil.setProperty(row, cinfo.bindName, val);
						}
						else {
							this.m_ViewCore.addErrorLog(`${this.dataType}データのプロパティが見つかりませんでした。[${this.m_Name}}][${cinfo.bindName}}]`);
						}
					}
				}
				idx++;
			}
		}
	}

	/**
	 * 値をクリアする
	 * @param listName リスト名
	 * @returns trueの場合、成功
	 */
	public clearValue(listName?: string): void {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const row of this.m_ListData) {
				for(const cinfo of this.m_ControlMap.values()) {
					cinfo.rowIndex = idx;
					if (cinfo.useFormControl === true) {
						if (cinfo.config.notClear === false) {
							cinfo.setControlValue(idx, '');
							cinfo.value = this.m_ViewCore.onGetListClearValue(cinfo.name, this.m_Name, row, cinfo.value);
						}
					}
				}
				idx++;
			}
		}
	}

	/**
	 * 入力チェック
	 * @param listName リスト名
	 * @return trueの場合、入力済みでエラーなし
	 */
	public isInput(listName?: string): boolean {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const row of this.m_ListData) {
				for(const cinfo of this.m_ControlMap.values()) {
					cinfo.rowIndex = idx;
					if (cinfo.useFormControl === true) {
						const rcinfo = cinfo.getRowControlInfo();
						if (rcinfo.isReadOnly() === false) {
							if (!rcinfo.value || rcinfo.isInvalid === true && rcinfo.validError !== null)
								return false;
						}
					}
				}
				idx++;
			}
		}
		return true;
	}

	/**
	 * クリア処理
	 */
	public clear(): void {
		this.m_ControlMap.forEach((cinfo, key, map) => {
			cinfo.clearControl();
		});
	}

	/**
	 * 行インデクス設定
	 * @param idx 行インデクス
	 */
	public setRowIndex(idx: number): void {
		this.m_ControlMap.forEach((cinfo, key, map) => {
			cinfo.rowIndex = idx;
		});
	}

	/**
	 * コントロール情報取得
	 * @param name コントロール名
	 * @returns コントロール情報
	 */
	public getRowControlInfo(name: string, index?: number): WprControlInfo {
		if (this.m_ControlMap.has(name) === true) {
			if (index !== undefined)
				this.m_ControlMap.get(name).rowIndex = index;
			return this.m_ControlMap.get(name).getRowControlInfo();
		}
		return null;
	}

	/**
	 * 入力チェック処理
	 * @param actionName アクション名称
	 * @param validError バリデーションエラー情報
	 * @param isControl コントロールチェックフラグ
	 * @param row 行情報
	 */
	public checkValidate(actionName: string, validError: WprValidError, isControl: boolean, row?: any) {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const rowData of this.m_ListData) {
				if (!row || row === rowData) {
					this.m_ControlMap.forEach((cinfo, key, map) => {
						cinfo.rowIndex = idx;
						const rcinfo = cinfo.getRowControlInfo();
						rcinfo.rowIndex = idx;
						rcinfo.checkValidate(actionName, validError, isControl, true);
					});
				}
				idx++;
			}
		}
	}

	/**
	 * 入力エラーリスト取得
	 * @param errorList 入力エラーリスト
	 */
	public getValidErrorList(errorList: WprValidError[]): void {
		if (this.m_ListData) {
			let idx: number = 0;
			for(const row of this.m_ListData) {
				this.m_ControlMap.forEach(cinfo => {
					cinfo.rowIndex = idx;
					const rcinfo = cinfo.getRowControlInfo();
					rcinfo.rowIndex = idx;
					if (rcinfo.validError != null)
						errorList.push(rcinfo.validError);
				});
				idx++;
			}
		}
	}
	// --------------------------------------------------------------------------
}
